/**
 * DayColumn — one day's vertical hour timeline. Hosts its timed {@link ActivityBlock}s, the hour grid
 * lines, and a palette drop target (dropping computes the start hour from the cursor Y). When the night
 * window is collapsed, fully-pre-06:00 blocks fold into a "night dot" (click expands the night window),
 * while blocks straddling 06:00 stay as clipped morning slices. A collapsed day shows just a count.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { HEAD_PAD, HOUR_PX, NIGHT_START, hoursToPx, type BoardPart } from '../model';
import { readDrag, type PalettePayload } from '../model';
import { cn } from '@/shared/lib/utils';
import { ActivityBlock } from './ActivityBlock';

type StyleVars = React.CSSProperties & Record<string, string | number>;

interface DayColumnProps {
  dayIdx: number;
  dayStart: number;
  dayHours: number;
  layout: { lanes: { part: BoardPart; lane: number }[]; laneCount: number };
  collapsed: boolean;
  readOnly: boolean;
  /** True only while a droppable palette drag (a non-accommodation placeholder) is active. */
  paletteDropActive: boolean;
  selectedUid: string | null;
  dragHover: { dayIdx: number; y: number } | null;
  setDragHover: (v: { dayIdx: number; y: number } | null) => void;
  onDropPalette: (payload: PalettePayload, dayIdx: number, startHour: number) => void;
  onMove: (uid: string, dayShift: number, hourShift: number) => void;
  onResize: (uid: string, hourDelta: number) => void;
  onOpen: (uid: string) => void;
  onExpandNight: () => void;
  onActDragChange: (dragging: boolean) => void;
}

export function DayColumn({
  dayIdx,
  dayStart,
  dayHours,
  layout,
  collapsed,
  readOnly,
  paletteDropActive,
  selectedUid,
  dragHover,
  setDragHover,
  onDropPalette,
  onMove,
  onResize,
  onOpen,
  onExpandNight,
  onActDragChange,
}: DayColumnProps) {
  const { t } = useTranslation('venua-planner');
  const [hoverY, setHoverY] = useState<number | null>(null);
  const nightCollapsed = dayStart > NIGHT_START;
  // Fully-night blocks (start AND end before the day window) collapse into a night-count dot; blocks that
  // straddle 06:00 stay as clipped morning slices; everything else renders normally.
  const nightItems = nightCollapsed
    ? layout.lanes.filter(({ part }) => (part.startHour ?? 0) + part.hours <= dayStart)
    : [];
  const blockItems = nightCollapsed
    ? layout.lanes.filter(({ part }) => (part.startHour ?? 0) + part.hours > dayStart)
    : layout.lanes;

  const height = HEAD_PAD + hoursToPx(dayHours);

  const handleDragOver = (event: React.DragEvent) => {
    // Only a droppable palette drag arms the timeline; accommodation drags and strip moves are ignored.
    if (collapsed || readOnly || !paletteDropActive) return;
    event.preventDefault();
    const rect = event.currentTarget.getBoundingClientRect();
    const y = event.clientY - rect.top;
    setHoverY(y);
    setDragHover({ dayIdx, y });
  };
  const clearHover = () => {
    setHoverY(null);
    setDragHover(null);
  };
  const handleDrop = (event: React.DragEvent) => {
    if (collapsed || readOnly || !paletteDropActive) return;
    event.preventDefault();
    const payload = readDrag(event);
    clearHover();
    if (!payload || payload.source !== 'palette') return;
    const rect = event.currentTarget.getBoundingClientRect();
    const y = event.clientY - rect.top;
    const startHour = dayStart + Math.max(0, (y - HEAD_PAD) / HOUR_PX);
    onDropPalette(payload, dayIdx, Math.round(startHour * 4) / 4);
  };

  const style: StyleVars = {
    height,
    minHeight: height,
    paddingTop: HEAD_PAD,
    '--lane-count': layout.laneCount || 1,
  };

  return (
    <div
      className={cn('tp-col', collapsed && 'tp-col--collapsed')}
      style={style}
      onDragOver={handleDragOver}
      onDragLeave={clearHover}
      onDrop={handleDrop}
    >
      <div className="tp-col-grid" aria-hidden="true" style={{ top: HEAD_PAD }}>
        {Array.from({ length: dayHours + 1 }, (_, i) => (
          <div key={i} className="tp-col-grid-line" style={{ top: hoursToPx(i) }} />
        ))}
      </div>
      {collapsed ? (
        <div className="tp-col-collapsed-hint" aria-hidden="true">
          {layout.lanes.length > 0 ? (
            <span className="tp-col-collapsed-count">{layout.lanes.length}</span>
          ) : null}
        </div>
      ) : (
        <>
          {nightItems.length > 0 ? (
            <button
              type="button"
              className="tp-night-dot"
              onClick={onExpandNight}
              title={t('costScheme.nightItemsTitle', { n: nightItems.length })}
              aria-label={t('costScheme.nightItemsTitle', { n: nightItems.length })}
            >
              {nightItems.length}
            </button>
          ) : null}
          {dragHover && dragHover.dayIdx === dayIdx && hoverY != null ? (
            <div className="tp-drop-indicator" style={{ top: Math.round(hoverY) }} />
          ) : null}
          {blockItems.map(({ part, lane }) => (
            <ActivityBlock
              key={part.uid}
              part={part}
              laneIndex={lane}
              laneCount={layout.laneCount || 1}
              dayStart={dayStart}
              readOnly={readOnly}
              selected={part.uid === selectedUid}
              onMove={onMove}
              onResize={onResize}
              onOpen={onOpen}
              onDragStateChange={onActDragChange}
            />
          ))}
        </>
      )}
    </div>
  );
}
